import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { EditorSelection, StateField } from "@codemirror/state";
import { showPanel } from "@codemirror/view";
import { Box, CssVarsProvider, Grid, IconButton, Tooltip, useTheme, } from "@mui/joy";
import { useMediaQuery } from "@mui/material";
import { createRoot } from "react-dom/client";
const toolbarState = StateField.define({
    create: () => true,
    update() {
        return true;
    },
    provide: (f) => showPanel.from(f, (on) => (on ? createToolbar : null)),
});
/**
 *
 * @param {EditorView} view view
 * @returns {object} toolbar
 */
function createToolbar(view) {
    const dom = document.createElement("div");
    const root = createRoot(dom);
    root.render(_jsx(CssVarsProvider, { children: _jsx(Toolbar, { view: view }) }));
    return { top: true, dom };
}
export function toolbar() {
    return [toolbarState];
}
const Toolbar = ({ view }) => {
    const theme = useTheme();
    const isDownSm = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
    return (_jsx(Box, { sx: {
            p: 0.5,
        }, children: _jsxs(Grid, { container: true, spacing: 1, children: [_jsx(Grid, { ml: isDownSm ? undefined : "auto", children: _jsx(Tooltip, { title: "Add bold text", children: _jsx(IconButton, { color: "neutral", variant: "plain", size: "sm", onMouseDown: (e) => {
                                e.preventDefault();
                                view.dispatch(view.state.changeByRange((range) => ({
                                    changes: [
                                        { from: range.from, insert: "**" },
                                        { from: range.to, insert: "**" },
                                    ],
                                    range: range.from === range.to
                                        ? EditorSelection.range(range.from + 2, range.from + 2)
                                        : EditorSelection.range(range.to + 4, range.to + 4),
                                })));
                            }, children: _jsx("i", { className: "fa-solid fa-bold" }) }) }) }), _jsx(Grid, { children: _jsx(Tooltip, { title: "Add italic text", children: _jsx(IconButton, { color: "neutral", variant: "plain", size: "sm", onMouseDown: (e) => {
                                e.preventDefault();
                                view.dispatch(view.state.changeByRange((range) => ({
                                    changes: [
                                        { from: range.from, insert: "*" },
                                        { from: range.to, insert: "*" },
                                    ],
                                    range: range.from === range.to
                                        ? EditorSelection.range(range.from + 1, range.from + 1)
                                        : EditorSelection.range(range.to + 2, range.to + 2),
                                })));
                            }, children: _jsx("i", { className: "fa-solid fa-italic" }) }) }) }), _jsx(Grid, { children: _jsx(Tooltip, { title: "Insert a quote", children: _jsx(IconButton, { color: "neutral", variant: "plain", size: "sm", onMouseDown: (e) => {
                                e.preventDefault();
                                view.dispatch(view.state.changeByRange((range) => ({
                                    changes: [{ from: range.from, insert: "> " }],
                                    range: range.from === range.to
                                        ? EditorSelection.range(range.from + 2, range.from + 2)
                                        : EditorSelection.range(range.to + 2, range.to + 2),
                                })));
                            }, children: _jsx("i", { className: "fa-solid fa-quote-right" }) }) }) }), _jsx(Grid, { children: _jsx(Tooltip, { title: "Insert code", children: _jsx(IconButton, { color: "neutral", variant: "plain", size: "sm", onMouseDown: (e) => {
                                e.preventDefault();
                                view.dispatch(view.state.changeByRange((range) => {
                                    const multiline = view.state.doc.slice(range.from, range.to).lines > 1;
                                    if (multiline) {
                                        return {
                                            changes: [
                                                { from: range.from, insert: "```\n" },
                                                { from: range.to, insert: "\n```" },
                                            ],
                                            range: EditorSelection.range(range.to + 8, range.to + 8),
                                        };
                                    }
                                    else
                                        return {
                                            changes: [
                                                { from: range.from, insert: "`" },
                                                { from: range.to, insert: "`" },
                                            ],
                                            range: range.from === range.to
                                                ? EditorSelection.range(range.from + 1, range.from + 1)
                                                : EditorSelection.range(range.to + 2, range.to + 2),
                                        };
                                }));
                            }, children: _jsx("i", { className: "fa-solid fa-code" }) }) }) }), _jsx(Grid, { children: _jsx(Tooltip, { title: "Insert a link", children: _jsx(IconButton, { color: "neutral", variant: "plain", size: "sm", onMouseDown: (e) => {
                                e.preventDefault();
                                view.dispatch(view.state.changeByRange((range) => ({
                                    changes: [
                                        { from: range.from, insert: "[" },
                                        { from: range.to, insert: "](url)" },
                                    ],
                                    range: range.from === range.to
                                        ? EditorSelection.range(range.from + 1, range.from + 1)
                                        : EditorSelection.range(range.to + 7, range.to + 7),
                                })));
                            }, children: _jsx("i", { className: "fa-solid fa-link-simple" }) }) }) }), _jsx(Grid, { children: _jsx(Tooltip, { title: "Add a bullet list", children: _jsx(IconButton, { color: "neutral", variant: "plain", size: "sm", onMouseDown: (e) => {
                                e.preventDefault();
                                view.dispatch(view.state.changeByRange((range) => ({
                                    changes: [{ from: range.from, insert: "* " }],
                                    range: EditorSelection.range(range.to + 2, range.to + 2),
                                })));
                            }, children: _jsx("i", { className: "fa-solid fa-list" }) }) }) }), _jsx(Grid, { children: _jsx(Tooltip, { title: "Add an ordered list", children: _jsx(IconButton, { color: "neutral", variant: "plain", size: "sm", onMouseDown: (e) => {
                                e.preventDefault();
                                view.dispatch(view.state.changeByRange((range) => ({
                                    changes: [{ from: range.from, insert: "1. " }],
                                    range: EditorSelection.range(range.to + 3, range.to + 3),
                                })));
                            }, children: _jsx("i", { className: "fa-solid fa-list-ol" }) }) }) }), _jsx(Grid, { children: _jsx(Tooltip, { title: "Add a task list", children: _jsx(IconButton, { color: "neutral", variant: "plain", size: "sm", onMouseDown: (e) => {
                                e.preventDefault();
                                view.dispatch(view.state.changeByRange((range) => ({
                                    changes: [{ from: range.from, insert: "* [ ] " }],
                                    range: EditorSelection.range(range.to + 6, range.to + 6),
                                })));
                            }, children: _jsx("i", { className: "fa-solid fa-list-check" }) }) }) }), _jsx(Grid, { children: _jsx(Tooltip, { title: "Add a table", children: _jsx(IconButton, { color: "neutral", variant: "plain", size: "sm", onMouseDown: (e) => {
                                e.preventDefault();
                                const table = `| header | header |\n| ------ | ------ |\n| cell | cell |\n| cell | cell |`;
                                view.dispatch(view.state.changeByRange((range) => ({
                                    changes: [{ from: range.from, insert: table }],
                                    range: EditorSelection.range(range.from, range.from),
                                })));
                            }, children: _jsx("i", { className: "fa-solid fa-table" }) }) }) })] }) }));
};
